import { useCallback, useState } from 'react';
import toast from 'react-hot-toast';
import {
  BlockPlayerPayload,
  CONNECTIONS_API,
  CONNECTION_BLOCKS_API,
  CONNECTION_REMOVE_API,
  CONNECTION_UNBLOCK_API,
  ConnectionActionResponse,
  CreateConnectionRequestPayload,
  PLAYED_WITH_DISMISS_API,
  PLAYED_WITH_RESTORE_API,
  PlayedWithDismissalPayload,
  RemoveConnectionPayload,
  getConnectionRequestAcceptApi,
  getConnectionRequestCancelApi,
  getConnectionRequestDeclineApi,
} from 'constants/payloads/connections';
import { useApiGateway } from 'hooks/useApi';

type ActionPayload =
  | CreateConnectionRequestPayload
  | RemoveConnectionPayload
  | BlockPlayerPayload
  | PlayedWithDismissalPayload
  | undefined;

interface Options {
  /** Re-read the summary once a write lands — pass `refetch` from `useConnections`. */
  onChanged?: () => void;
}

/**
 * The write half of connections. The tables are select-only for the `user` role,
 * so each action posts to an API route that authorizes the actor server-side; on
 * success the caller's summary query is re-read rather than the cache patched by
 * hand, since one write can change several of its lists at once (a block, for
 * instance, also drops a connection and cancels pending requests).
 */
export const useConnectionActions = ({ onChanged }: Options = {}) => {
  const { post } = useApiGateway<ActionPayload, ConnectionActionResponse>();
  const [pendingUserId, setPendingUserId] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const run = useCallback(
    async ({
      endpoint,
      payload,
      errorMessage,
      successMessage,
      trackingId,
    }: {
      endpoint: string;
      payload?: ActionPayload;
      errorMessage: string;
      successMessage?: string;
      /** The player the action is about, so a row can show its own spinner. */
      trackingId?: string | null;
    }): Promise<boolean> => {
      setIsSubmitting(true);
      setPendingUserId(trackingId ?? null);

      try {
        const response = await post({ endpoint, payload });

        if (response.isError) {
          toast.error(response.error || errorMessage);
          // Usually the request changed underneath (withdrawn, already answered).
          onChanged?.();
          return false;
        }

        if (successMessage) {
          toast.success(successMessage);
        }

        onChanged?.();
        return true;
      } finally {
        setIsSubmitting(false);
        setPendingUserId(null);
      }
    },
    [onChanged, post],
  );

  const sendRequest = useCallback(
    (recipientUserId: string, message?: string) =>
      run({
        endpoint: CONNECTIONS_API,
        payload: { recipientUserId, message },
        errorMessage: 'Could not send your request',
        successMessage: 'Request sent',
        trackingId: recipientUserId,
      }),
    [run],
  );

  const acceptRequest = useCallback(
    (requestId: string) =>
      run({
        endpoint: getConnectionRequestAcceptApi(requestId),
        errorMessage: 'Could not accept this request',
        successMessage: "You're connected",
      }),
    [run],
  );

  const declineRequest = useCallback(
    (requestId: string) =>
      run({
        endpoint: getConnectionRequestDeclineApi(requestId),
        errorMessage: 'Could not decline this request',
      }),
    [run],
  );

  const cancelRequest = useCallback(
    (requestId: string) =>
      run({
        endpoint: getConnectionRequestCancelApi(requestId),
        errorMessage: 'Could not cancel this request',
      }),
    [run],
  );

  const removeConnection = useCallback(
    (otherUserId: string) =>
      run({
        endpoint: CONNECTION_REMOVE_API,
        payload: { otherUserId },
        errorMessage: 'Could not remove this connection',
        successMessage: 'Connection removed',
        trackingId: otherUserId,
      }),
    [run],
  );

  const blockPlayer = useCallback(
    (blockedUserId: string) =>
      run({
        endpoint: CONNECTION_BLOCKS_API,
        payload: { blockedUserId },
        errorMessage: 'Could not block this player',
        successMessage: 'Player blocked',
        trackingId: blockedUserId,
      }),
    [run],
  );

  const unblockPlayer = useCallback(
    (blockedUserId: string) =>
      run({
        endpoint: CONNECTION_UNBLOCK_API,
        payload: { blockedUserId },
        errorMessage: 'Could not unblock this player',
        successMessage: 'Player unblocked',
        trackingId: blockedUserId,
      }),
    [run],
  );

  const dismissPlayedWith = useCallback(
    (dismissedUserId: string) =>
      run({
        endpoint: PLAYED_WITH_DISMISS_API,
        payload: { dismissedUserId },
        errorMessage: 'Could not remove this player',
        trackingId: dismissedUserId,
      }),
    [run],
  );

  const restorePlayedWith = useCallback(
    (dismissedUserId: string) =>
      run({
        endpoint: PLAYED_WITH_RESTORE_API,
        payload: { dismissedUserId },
        errorMessage: 'Could not restore this player',
        trackingId: dismissedUserId,
      }),
    [run],
  );

  return {
    isSubmitting,
    /** Set while an action runs, when that action is about one specific player. */
    pendingUserId,
    sendRequest,
    acceptRequest,
    declineRequest,
    cancelRequest,
    removeConnection,
    blockPlayer,
    unblockPlayer,
    dismissPlayedWith,
    restorePlayedWith,
  };
};

export type ConnectionActions = ReturnType<typeof useConnectionActions>;
